import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Banknote, ChevronDown, Clock, CheckCircle2, FileDown, HandCoins, Landmark, Receipt, Sheet } from "lucide-react";
import { useApp } from "../context/AppContext";
import { authHeaders } from "../context/AuthContext";
import { SAR, downloadAuthed } from "../souq";
import BankDetailsCard from "./BankDetailsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const monthLabel = (m, lang) => {
  const [y, mo] = m.split("-").map(Number);
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString(lang === "ar" ? "ar-SA-u-ca-gregory-nu-latn" : "en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
};

export function PayoutStatus({ status, t }) {
  const done = status === "transferred";
  return (
    <span className={`chip ${done ? "chip-ok" : "chip-warn"} po-status`} data-testid={`payout-status-${status}`}>
      {done ? <CheckCircle2 size={12} /> : <Clock size={12} />} {t(`po.status.${status}`)}
    </span>
  );
}

function OrderRow({ r, lang, t }) {
  const dl = () => downloadAuthed(`${API}/souq/orders/${r.order_id}/invoice.pdf`, `settlement-${r.order_id}.pdf`, authHeaders()).catch(() => toast.error(t("po.dlFail")));
  return (
    <tr data-testid={`payout-order-${r.order_id}`}>
      <td dir="ltr">{(r.paid_at || "").slice(0, 10)}</td>
      <td className="po-mono" dir="ltr">{r.order_id}</td>
      <td>{lang === "ar" ? r.variety_ar : r.variety_en} · <span dir="ltr">{r.quantity_kg} kg</span></td>
      <td>{r.buyer_name}</td>
      <td dir="ltr">{SAR(r.amount, lang)}</td>
      <td dir="ltr" className="po-dim">−{SAR(r.fees, lang)}</td>
      <td dir="ltr" className="po-gold">{SAR(r.net, lang)}</td>
      <td><button className="btn-lux-ghost po-pdf" onClick={dl} data-testid={`payout-pdf-${r.order_id}`}><FileDown size={13} /> PDF</button></td>
    </tr>
  );
}

function MonthRow({ b, lang, t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`po-month ${open ? "open" : ""}`} data-testid={`payout-month-${b.month}`}>
      <button className="po-month-head" onClick={() => setOpen((o) => !o)} data-testid={`payout-month-toggle-${b.month}`} aria-expanded={open}>
        <ChevronDown size={16} className="po-chev" />
        <span className="po-month-name">{monthLabel(b.month, lang)}</span>
        <span className="po-month-meta">{b.orders_count} {t("po.orders")}</span>
        <span className="po-month-meta" dir="ltr">{SAR(b.gross, lang)}</span>
        <span className="po-month-net" dir="ltr">{SAR(b.net, lang)}</span>
        <PayoutStatus status={b.status} t={t} />
      </button>
      {open && (
        <div className="po-month-body">
          {b.status === "transferred" && (
            <p className="po-transfer-note" data-testid={`payout-transfer-info-${b.month}`}>
              <CheckCircle2 size={14} /> {t("po.transferredOn")} <span dir="ltr">{(b.transferred_at || "").slice(0, 10)}</span>
              {b.reference && <> · {t("po.reference")}: <span dir="ltr" className="po-mono">{b.reference}</span></>}
            </p>
          )}
          <div className="an-table-wrap">
            <table className="an-table po-table">
              <thead><tr><th>{t("po.paidAt")}</th><th>{t("po.order")}</th><th>{t("an.variety")}</th><th>{t("po.buyer")}</th><th>{t("an.gross")}</th><th>{t("an.fees")}</th><th>{t("an.net")}</th><th>{t("po.settlement")}</th></tr></thead>
              <tbody>{b.orders.map((r) => <OrderRow key={r.order_id} r={r} lang={lang} t={t} />)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FarmerPayouts() {
  const { t, lang } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payouts/farmer`, { headers: authHeaders() }).then((r) => setData(r.data)).catch(() => setData({ months: [], totals: {} }));
  }, []);

  const exportCsv = () => downloadAuthed(`${API}/payouts/farmer.csv`, "payouts.csv", authHeaders()).then(() => toast.success(t("po.csvOk"))).catch(() => toast.error(t("po.dlFail")));

  if (!data) return <section className="dash-panel"><div className="an-empty">…</div></section>;

  const cards = [
    [HandCoins, t("po.totalNet"), SAR(data.totals.net, lang), "total"],
    [Clock, t("po.pending"), SAR(data.totals.pending, lang), "pending"],
    [CheckCircle2, t("po.transferred"), SAR(data.totals.transferred, lang), "transferred"],
    [Receipt, t("po.paidOrders"), data.totals.orders || 0, "orders"],
  ];

  return (
    <section className="dash-panel" data-testid="farmer-payouts">
      <div className="an-head">
        <h2 className="dash-panel-title"><Banknote size={18} /> {t("po.title")}</h2>
        {data.months.length > 0 && (
          <button className="btn-lux-ghost" onClick={exportCsv} data-testid="payouts-export-csv"><Sheet size={14} /> {t("po.exportCsv")}</button>
        )}
      </div>
      <p className="po-hint">{t("po.hint")}</p>
      {data.months.length > 0 && !data.has_bank && (
        <div className="bank-nudge" role="status" data-testid="bank-nudge"><Landmark size={16} /> <span>{t("bank.nudge")}</span></div>
      )}
      <BankDetailsCard />
      {!data.months.length ? (
        <div className="an-empty" data-testid="payouts-empty">{t("po.empty")}</div>
      ) : (
        <>
          <div className="an-cards">
            {cards.map(([Icon, label, value, id]) => (
              <div className="an-card" key={id} data-testid={`payout-card-${id}`}>
                <Icon size={16} className="an-card-ic" /><span className="an-card-val">{value}</span><span className="an-card-label">{label}</span>
              </div>
            ))}
          </div>
          <div className="po-ledger" data-testid="payouts-ledger">
            {data.months.map((b) => <MonthRow key={b.month} b={b} lang={lang} t={t} />)}
          </div>
        </>
      )}
    </section>
  );
}
